import { apiServiceInstance } from '@/core/helpers/utils';
import {
  ApiResponse,
  IDinhMucCoDinhNewResponseBase,
  IDonViPdfExtra,
  IVatTuResponseBase,
  PaginationResponseBase,
} from '../interfaces/ApiResponses';
import { IPaginationParams } from '../interfaces/Models';
import type {
  DinhMucCoDinhCheckRequest,
  DinhMucCoDinhCheckResult,
  DinhMucCoDinhCreateRequest,
  DinhMucCoDinhUpdateRequest,
} from '@/types/dinh-muc-co-dinh';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export interface IDinhMucCoDinhFilterParams extends IPaginationParams {
  id_cap_sc?: number;
  id_dm_toa_xe?: number;
  status?: string;
}

export class DinhMucCoDinhService {
  static get(id: number): Promise<{
    data: ApiResponse<IDinhMucCoDinhNewResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/dinh-muc-co-dinh/chi-tiet/${id}`
    );
  }

  static async listAll(
    filters: Omit<IDinhMucCoDinhFilterParams, 'current_page' | 'per_page'> = {},
    per_page = 200
  ): Promise<{
    items: IDinhMucCoDinhNewResponseBase[];
    extra?: IDonViPdfExtra;
  }> {
    const allItems: IDinhMucCoDinhNewResponseBase[] = [];
    let page = 1;
    let total = Infinity;
    let extra: IDonViPdfExtra | undefined;

    while (allItems.length < total) {
      const { data: response } = await DinhMucCoDinhService.getList({
        ...filters,
        current_page: page,
        per_page,
      });
      const {
        data: { data, total: totalItems },
      } = response;

      if (page === 1) {
        extra = response.extra as IDonViPdfExtra | undefined;
      }

      total = totalItems;
      allItems.push(...data);

      if (!data.length) break;
      page += 1;
    }

    return { items: allItems, extra };
  }

  static getList({
    id_cap_sc,
    id_dm_toa_xe,
    current_page,
    per_page,
    status,
  }: IDinhMucCoDinhFilterParams): Promise<{
    data: ApiResponse<PaginationResponseBase<IDinhMucCoDinhNewResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/dinh-muc-co-dinh/danh-sach',
      {
        params: {
          page: current_page || 1,
          ...(id_cap_sc && {
            id_cap_sc,
          }),
          ...(id_dm_toa_xe && {
            id_dm_toa_xe,
          }),
          ...(status && {
            status,
          }),
        },
        headers: {
          perpage: per_page || 1000,
        },
      }
    );
  }

  static async getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
    id_cap_sc,
    id_dm_toa_xe,
    status,
  }: {
    id_cap_sc: number;
    id_dm_toa_xe: number;
    status?: string;
  }): Promise<IDinhMucCoDinhNewResponseBase | null> {
    const {
      data: {
        data: { data },
      },
    } = await this.getList({ id_cap_sc, id_dm_toa_xe, status });

    if (data.length) return data[0];

    return null;
  }

  static async attachVatTuItems({
    id_dinh_muc,
    vat_tu_items,
  }: {
    id_dinh_muc: number;
    vat_tu_items: IVatTuResponseBase[];
  }) {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';

    const bodyFormData = new FormData();

    vat_tu_items.forEach(({ id, so_luong }) => {
      bodyFormData.append(`dmVatTu[${id}][so_luong]`, so_luong.toString());
    });

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/dinh-muc-co-dinh/${id_dinh_muc}/vat-tu`,
      bodyFormData
    );
  }

  /** POST /tao-moi — unified create `{ dinhMucCoDinh, dmVatTu }` */
  static create(payload: DinhMucCoDinhCreateRequest): Promise<{
    data: ApiResponse<IDinhMucCoDinhNewResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/dinh-muc-co-dinh/tao-moi',
      payload,
      { headers: JSON_HEADERS }
    );
  }

  /** POST /kiem-tra — dry-run before create/update (does not write) */
  static check(payload: DinhMucCoDinhCheckRequest): Promise<{
    data: ApiResponse<DinhMucCoDinhCheckResult>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/dinh-muc-co-dinh/kiem-tra',
      payload,
      { headers: JSON_HEADERS }
    );
  }

  static delete(id: number) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/dinh-muc-co-dinh/xoa/${id}`
    );
  }

  /** POST /cap-nhat/{id} — unified update `{ dinhMucCoDinh, dmVatTu }` */
  static update(
    id: number,
    payload: DinhMucCoDinhUpdateRequest
  ): Promise<{
    data: ApiResponse<IDinhMucCoDinhNewResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/dinh-muc-co-dinh/cap-nhat/${id}`,
      payload,
      { headers: JSON_HEADERS }
    );
  }
}
